import { Check, ChevronRight } from 'lucide-react';
import { tx } from '../i18n';

interface SearchStepsProps {
  /** 1 while the postcode is being entered, 2 once the addresses are listed. */
  current: 1 | 2;
  /** The postcode that was searched, shown on the completed first step. */
  postcode?: string;
  /** Back to step one, with the postcode still in the box. */
  onChangePostcode?: () => void;
}

/**
 * Where the reader is in the two-step search, and how to go back.
 *
 * <p>"Step 2 of 2" on its own is a claim with nothing to check it against: the first step has
 * scrolled away and there is no sign it was ever completed, or how to return to it. Both steps are
 * drawn, the finished one carries a tick and the postcode it found, and it is a button - because
 * the commonest reason to look at this line at all is having typed the wrong postcode.
 */
export default function SearchSteps({ current, postcode, onChangePostcode }: SearchStepsProps) {
  const done = current > 1;

  return (
    <nav className="search-steps" aria-label={tx('Search progress', 'Arama ilerlemesi', 'Postęp wyszukiwania', 'Progresso da pesquisa', 'Progreso de la búsqueda')}>
      <ol>
        <li className={done ? 'search-step search-step--done' : 'search-step search-step--current'}>
          {onChangePostcode && done ? (
            <button type="button" className="search-step__button" onClick={onChangePostcode}>
              <span className="search-step__mark" aria-hidden="true">
                <Check size={13} />
              </span>
              <span>
                {tx('Postcode', 'Posta kodu', 'Kod pocztowy', 'Código postal', 'Código postal')}
                {postcode ? <strong className="search-step__value">{postcode}</strong> : null}
              </span>
              <span className="search-step__change">
                {tx('Change', 'Değiştir', 'Zmień', 'Alterar', 'Cambiar')}
              </span>
            </button>
          ) : (
            <span className="search-step__button">
              <span className="search-step__mark" aria-hidden="true">
                1
              </span>
              <span>{tx('Postcode', 'Posta kodu', 'Kod pocztowy', 'Código postal', 'Código postal')}</span>
            </span>
          )}
        </li>

        <li aria-hidden="true" className="search-steps__arrow">
          <ChevronRight size={16} />
        </li>

        <li
          className={current === 2 ? 'search-step search-step--current' : 'search-step'}
          aria-current={current === 2 ? 'step' : undefined}
        >
          <span className="search-step__button">
            <span className="search-step__mark" aria-hidden="true">
              2
            </span>
            <span>{tx('Property', 'Konut', 'Lokal', 'Imóvel', 'Vivienda')}</span>
          </span>
        </li>
      </ol>
    </nav>
  );
}
